import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import BiometricButton from './BiometricButton';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/main-dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    // Simulate network delay for realistic feel
    setTimeout(() => {
      const result = login(email, password);
      
      if (result.success) {
        navigate('/main-dashboard');
      } else {
        setErrors({
          general: 'Invalid email or password. Please try again.'
        });
        setIsLoading(false);
      }
    }, 800);
  };

  const handleBiometricAuth = () => {
    // For demo, biometric auth is disabled
    setErrors({
      general: 'Biometric authentication is not available for this demo.'
    });
  };

  const handleEmailChange = (e) => {
    setEmail(e?.target?.value);
    if (errors?.email || errors?.general) {
      setErrors({ ...errors, email: '', general: '' });
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e?.target?.value);
    if (errors?.password || errors?.general) {
      setErrors({ ...errors, password: '', general: '' });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 md:px-6">
      <div className="bg-card rounded-[16px] p-6 md:p-8 lg:p-10 border border-border shadow-elevation-2 backdrop-blur-sm">
        <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
          {errors?.general && (
            <div className="p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
              {errors.general}
            </div>
          )}
          
          <Input
            type="email"
            label="Email Address"
            placeholder="you@jeirotex.com"
            value={email}
            onChange={handleEmailChange}
            error={errors?.email}
            required
            disabled={isLoading}
            className="transition-smooth"
          />

          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={handlePasswordChange}
              error={errors?.password}
              required
              disabled={isLoading}
              className="transition-smooth"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e?.target?.checked)}
                disabled={isLoading}
                className="w-4 h-4 rounded border-border bg-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth cursor-pointer"
              />
              <span className="text-sm text-foreground group-hover:text-primary transition-smooth">
                Remember me
              </span>
            </label>
            <button
              type="button"
              className="text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
              disabled={isLoading}
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            className="h-12 md:h-14 text-base md:text-lg font-semibold shadow-glow-primary hover:scale-[1.02] transition-smooth"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="relative my-6 md:my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs md:text-sm">
              <span className="px-3 bg-card text-muted-foreground caption">Or continue with</span>
            </div>
          </div>

          <BiometricButton 
            onAuthenticate={handleBiometricAuth}
            disabled={isLoading}
          />
        </form>

        <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border text-center">
          <p className="text-sm md:text-base text-muted-foreground">
            Access restricted to authorized investors only
          </p>
        </div>
      </div>
      <div className="mt-6 md:mt-8 text-center px-4">
        <p className="text-xs md:text-sm text-muted-foreground caption leading-relaxed">
          By signing in, you agree to our{' '}
          <button className="text-primary hover:text-primary/80 transition-smooth font-medium">
            Terms of Service
          </button>{' '}
          and{' '}
          <button className="text-primary hover:text-primary/80 transition-smooth font-medium">
            Privacy Policy
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
